import React, { useContext } from "react";
import { unthunk, type Thunk } from "state-trees/src/utils";
import type { IDocsContext } from "./chrome/DocsContext";
import { DocsContext } from "./chrome/DocsContext";
import type { DocsVersion } from "./chrome/FullMetaBlob";
import { currentDocsVersion } from "./chrome/nav/DocsVersionSelector";
import { useDocsLocation } from "./chrome/nav/useDocsLocation";
import { DocLink } from "./markdown-components";

interface TutorialTopic {
  name: string;
  href: Thunk<string, [DocsVersion, IDocsContext]>;
}

export const TutorialTopics: { [key: string]: TutorialTopic } = {
  // beginner tutorial topics: topics to be displayed on the Learn to use Gadget tutorials
  Shopify: {
    name: "Shopify connections",
    href: "/guides/plugins/shopify",
  },
  Modelling: { name: "Building models", href: (version) => (version.id == "effects" ? "/guides/building-models" : "/guides/models") },
  Relationships: {
    name: "Relationships",
    href: (version) =>
      version.id == "effects"
        ? "/guides/building-models#relationships-and-relationship-fields"
        : "/guides/models#relationships-and-relationship-fields",
  },
  Actions: {
    name: "Actions",
    href: (version) => (version.id == "effects" ? "/guides/extending-with-code" : "/guides/actions"),
  },

  // advanced tutorial topics: topics to be displayed on the Advanced building with Gadget
  ShopifyEmbedded: {
    name: "Embedded Shopify apps",
    href: (version) =>
      version.id == "effects" ? "/guides/plugins/building-shopify-apps/shopify-app-frontends" : "/guides/plugins/shopify/frontends",
  },
  HttpRoutes: { name: "HTTP routes", href: "/guides/http-routes" },
  StoringFiles: { name: "Storing files", href: "/guides/storing-files" },
  AccessControl: { name: "Access control", href: "/guides/access-control" },
  AI: {
    name: "AI + vector embeddings",
    href: (version) => (version.id == "effects" ? "/guides/building-ai-apps" : "/guides/plugins/openai/building-ai-apps"),
  },
  Auth: {
    name: "Authentication",
    href: (version) => (version.id == "effects" ? "/guides/authentication" : "/guides/plugins/authentication"),
  },

  // frontend
  Frontend: { name: "React frontends", href: (version) => (version.id == "effects" ? "/guides/building-frontends" : "/guides/frontend") },
};

export const TutorialTopicsDocLinks = (props: { topics: TutorialTopic[] }) => {
  const context = useContext(DocsContext);
  const [location] = useDocsLocation();
  const currentVersion = currentDocsVersion(location, context.currentApp);

  return props.topics.map((topic, i) => (
    <span key={topic.name}>
      <DocLink href={unthunk(topic.href, currentVersion, context)}>{topic.name}</DocLink>
      {i < props.topics.length - 1 && ", "}
    </span>
  ));
};

export const TutorialTopicsMap = {
  Quickstart: [TutorialTopics.Modelling, TutorialTopics.Actions, TutorialTopics.Frontend],
  ProductTagger: [TutorialTopics.Shopify, TutorialTopics.Modelling, TutorialTopics.Actions, TutorialTopics.Frontend],
  Middleware: [TutorialTopics.Shopify, TutorialTopics.Modelling, TutorialTopics.Actions],
  Blog: [TutorialTopics.Auth, TutorialTopics.Frontend],
  Quiz: [
    TutorialTopics.ShopifyEmbedded,
    { name: "Extend Shopify theme", href: "/guides/tutorials/product-recommendation-quiz-app#installing-in-a-shopify-theme" },
    TutorialTopics.AccessControl,
    TutorialTopics.StoringFiles,
  ],
  PostPurchaseUpsell: [TutorialTopics.HttpRoutes],
  Bundles: [TutorialTopics.ShopifyEmbedded, TutorialTopics.AccessControl, TutorialTopics.StoringFiles],
  CheckoutUI: [TutorialTopics.Shopify, TutorialTopics.Frontend],
  AIProductRecommender: [TutorialTopics.Shopify, TutorialTopics.AI, TutorialTopics.HttpRoutes],
  WebhookSubscription: [TutorialTopics.HttpRoutes],
  AIScreenwriter: [TutorialTopics.AI, TutorialTopics.Actions, TutorialTopics.HttpRoutes, TutorialTopics.Frontend],
};

export function getTopicNames(topics: TutorialTopic[]): string[] {
  return topics.map((topic) => topic.name);
}
